import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import App from "./App";
import WinsApp from "./wins-app/WinsApp";
import CallsApp from "./calls-app/CallsApp";
import StoriesApp from "./stories-app/StoriesApp";
import { PublicClipPage, PublicClipsListPage } from "./calls-app/PublicClipPages";
import AdminDashboard from "./admin/AdminDashboard";
import "./styles/globals.css";

// Host-based routing: same bundle serves multiple surfaces off the same
// Railway service.
//   winsboard.*                  → public, read-only "Felix Wins" wall (no SSO)
//   felixwins.*                  → authenticated editor (post / edit / delete)
//   felixwins.* + ?view=1        → authed user in board (view-only) mode
//   felixwins.* + ?page=calls    → authed call recordings + transcripts
//   felixwins.* + ?page=stories  → Lead Story tab (cards linking to public story pages)
//   ?clip=<id>                   → public single-clip viewer (no SSO, any host)
//   ?tab=clips on a public host  → public clips list (no SSO)
//   everything else              → existing command center
// ?board=1 / ?wins=1 / ?calls=1 are local-dev escape hatches.
function pickRoot():
  | "command-center" | "wins" | "wins-board" | "wins-embed" | "calls" | "stories"
  | "public-clip" | "public-clips-list" | "admin"
{
  const host = window.location.hostname;
  const params = new URLSearchParams(window.location.search);
  // Clip share links always render as the public single-clip viewer, no
  // matter which host the recipient lands on.
  // Admin dashboard: ?admin=1 on any authenticated host.
  if (params.get("admin") === "1") return "admin";
  if (params.get("clip")) return "public-clip";
  const onPublicBoard = host.startsWith("winsboard.") || params.get("board") === "1";
  // ?tab=clips on the public board (or with the ?board=1 escape hatch)
  // surfaces the public clips list.
  if (onPublicBoard && params.get("tab") === "clips") return "public-clips-list";
  if (onPublicBoard) return "wins-board";
  const isWinsHost = host.startsWith("felixwins.") || host.startsWith("surprising-tranquility") || params.get("wins") === "1";
  if (isWinsHost) {
    if (params.get("page") === "calls" || params.get("calls") === "1") return "calls";
    if (params.get("page") === "stories") return "stories";
    if (params.get("view") === "1") return "wins-board";
    // ?embed=1 renders the full authenticated app in a chromeless layout
    // suitable for <iframe> embedding on other internal pages.
    if (params.get("embed") === "1") return "wins-embed";
    return "wins";
  }
  return "command-center";
}
const root = pickRoot();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 1000 * 60 * 60 * 24 * 7, // 7d — persisted cache expiry
      refetchOnWindowFocus: false,
      // Always revalidate on mount even if we have a persisted cache. The
      // persisted data still shows instantly (cache-then-network); this just
      // forces a background refetch so a stale snapshot can't haunt a user.
      // (We had a real incident where someone who visited when there were 0
      // wins kept seeing the empty state forever — fix: don't persist empty
      // arrays + always refetch on mount.)
      refetchOnMount: "always",
    },
  },
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: "felix-os-rq-v1",
  throttleTime: 1000,
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24 * 7, // 7d
        // Bumped to v2 — invalidates any persisted "wins-v2: []" caches from
        // pre-launch visits that were trapping users on the empty state.
        buster: "v2",
        dehydrateOptions: {
          shouldDehydrateQuery: (q) => {
            const key = String(q.queryKey[0] ?? "");
            if (!["phases", "deliverables", "risks", "wins", "decisions", "pulse", "ttv", "criteria", "artifacts", "wins-v2"].includes(key)) return false;
            // Don't persist empty arrays — they'd hydrate as "no data" on the
            // next visit even after the server has real data. Let the empty
            // state refetch fresh every time.
            const data = q.state.data;
            if (Array.isArray(data) && data.length === 0) return false;
            return true;
          },
        },
      }}
    >
      {root === "admin" ? <AdminDashboard />
        : root === "public-clip" ? <PublicClipPage />
        : root === "public-clips-list" ? <PublicClipsListPage />
        : root === "calls" ? <CallsApp />
        : root === "stories" ? <StoriesApp />
        : root === "wins-board" ? <WinsApp boardMode />
        : root === "wins-embed" ? <WinsApp embedMode />
        : root === "wins" ? <WinsApp />
        : <App />}
    </PersistQueryClientProvider>
  </React.StrictMode>
);
